import React, { useState } from 'react'
import classNames from 'classnames'

import css from './RegItemCard.styles.scss'

import { RegItemCardProps } from './RegItemCard.types'
import { BuyingOptions } from './components/BuyingOptions/BuyingOptions'
import { RegItemControls } from './components/Controls/Controls'
import { ProductDescription } from './components/ProductDescription/ProductDescription'
import { ProductNotes } from './components/ProductNotes/ProductNotes'
import DragHandle from '../../icons/DragHandle.svg'
import Image from '../../../components/image'

const RegItemCard: React.FC<RegItemCardProps> = ({
  product,
  categories = [],
  size,
  onCategoryChange = () => {},
  onEditClick = () => {},
  onDeleteClick = () => {},
  badge,
  className,
  ...props
}) => {
  const isDraggingEnabled = props.draggable === true

  const RegItemCardClasses = classNames(
    css.RegItemCard,
    {
      [css.reg_item_card__desktop]: size === 'desktop',
      [css.reg_item_card__tablet]: size === 'tablet',
      [css.reg_item_card__phone]: size === 'phone',
      [css.reg_item_card__is_draggable]: isDraggingEnabled,
    },
    className
  )
  const image = (
    <div className={css.reg_item_card__image_container}>
      {product.image ? (
        <Image src={product.image} alt={product.heading} />
      ) : (
        <div className={css.reg_item_card__image_placeholder} />
      )}
      {badge && <div className={css.reg_item_card__image_badge}>{badge}</div>}
    </div>
  )

  const description = <ProductDescription size={size} product={product} />
  const controls = (
    <RegItemControls
      size={size}
      defaultCategory={product.category}
      categories={categories}
      onCategoryChange={onCategoryChange}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
    />
  )

  const [isDraggable, setIsDraggable] = useState<boolean>(false)
  const decoratedOnDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    const { onDragEnd = () => {} } = props
    onDragEnd(e)
    setIsDraggable(false)
  }

  if (size === 'desktop') {
    return (
      <div
        className={RegItemCardClasses}
        {...props}
        draggable={isDraggingEnabled && isDraggable}
        onDragEnd={decoratedOnDragEnd}
        tabIndex={-1}
      >
        <div className={css.reg_item_card__content}>
          <div className={css.reg_item_card__image}>{image}</div>
          <div className={css.reg_item_card__description}>{description}</div>
          <div className={css.reg_item_card__options}>
            <BuyingOptions options={product.offers} />
            {controls}
          </div>
        </div>
        {isDraggingEnabled && (
          <button
            className={css.reg_item_card__drag_handle}
            onMouseDown={() => setIsDraggable(true)}
            onMouseUp={() => setIsDraggable(false)}
            aria-label="Drag Handle"
          >
            <DragHandle />
          </button>
        )}
      </div>
    )
  } else {
    return (
      <div className={RegItemCardClasses} {...props} tabIndex={-1}>
        <div className={css.reg_item_card__content}>
          <div className={css.reg_item_card__image}>{image}</div>
          <div className={css.reg_item_card__description}>
            {description}
            {controls}
          </div>
          {product.notes && (
            <div className={css.reg_item_card__notes}>
              <ProductNotes notes={product.notes} />
            </div>
          )}
        </div>
      </div>
    )
  }
}

export default RegItemCard
